import { cn } from '@goaltree/ui/lib/utils'
import {
  CalendarCheck,
  CalendarClock,
  CalendarX,
  CheckCircle2,
  Circle,
  CircleDashed,
  CircleDot,
  Flag,
  FlagOff,
  History,
  Info,
  PencilLine,
  Pin,
  PinOff,
  SkipForward,
  Trash2,
} from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { UrgentFlag } from '@/components/task-meta'
import { haptic } from '@/lib/haptics'

// Long-press (touch/pen/mouse-hold) + right-click detector that opens a context
// menu at the pointer. Suppresses the click that follows a fired long-press so
// the row doesn't also toggle, and cancels if the pointer moves (a scroll/drag).
export function useLongPressMenu(opts: { disabled?: boolean } = {}) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggered = useRef(false)
  const startPos = useRef<{ x: number; y: number } | null>(null)

  const clear = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }
  // Android Chrome turns a press-and-hold into a text selection (and its "Touch
  // to Search" bar), grabbing whatever text it can reach, even the bottom nav.
  // Block selection for the whole hold, released a beat after lift.
  const unblock = useRef<(() => void) | null>(null)
  const blockSelection = () => {
    if (unblock.current) return
    const stop = (e: Event) => e.preventDefault()
    document.addEventListener('selectstart', stop)
    unblock.current = () => {
      document.removeEventListener('selectstart', stop)
      unblock.current = null
    }
  }
  const release = () => {
    clear()
    setTimeout(() => unblock.current?.(), 300)
  }

  return {
    menu,
    closeMenu: () => setMenu(null),
    // Call at the top of the row's onClick — returns true (and swallows the
    // event) when the click is the tail of a long-press that already fired.
    suppressClick: (e: { preventDefault: () => void; stopPropagation: () => void }) => {
      if (triggered.current) {
        triggered.current = false
        e.preventDefault()
        e.stopPropagation()
        return true
      }
      return false
    },
    longPressHandlers: {
      'data-long-press': '',
      onPointerDown: (e: React.PointerEvent) => {
        if (opts.disabled) return
        // Desktop opens the menu via right-click (onContextMenu); only touch/pen
        // use a press-and-hold, so mouse clicks never conflict with inline edit.
        if (e.pointerType === 'mouse') return
        triggered.current = false
        startPos.current = { x: e.clientX, y: e.clientY }
        clear()
        blockSelection()
        const { clientX, clientY } = e
        timer.current = setTimeout(() => {
          triggered.current = true
          window.getSelection()?.removeAllRanges()
          haptic('medium')
          setMenu({ x: clientX, y: clientY })
        }, 450)
      },
      onPointerMove: (e: React.PointerEvent) => {
        if (!startPos.current) return
        if (
          Math.abs(e.clientX - startPos.current.x) > 10 ||
          Math.abs(e.clientY - startPos.current.y) > 10
        )
          clear()
      },
      onPointerUp: release,
      onPointerLeave: release,
      onPointerCancel: release,
      onContextMenu: (e: React.MouseEvent) => {
        e.preventDefault()
        if (!opts.disabled) setMenu({ x: e.clientX, y: e.clientY })
      },
    },
  }
}

type Props = {
  pos: { x: number; y: number }
  onClose: () => void
  // Shown as the menu's header so a long-press makes clear which task it's for.
  title?: string
  canPin: boolean
  isDone: boolean
  isInProgress: boolean
  isUrgent: boolean
  isSuperUrgent?: boolean
  onSuperUrgent?: () => void
  isPinned: boolean
  onInProgress: () => void
  onPin: () => void
  onComplete: () => void
  onSkip?: () => void
  onUrgent: () => void
  // "Must do" lane actions; absent for habits.
  mustDo?: { committed: boolean; onCommit: (day: 'today' | 'tomorrow' | null) => void }
  // Habits: log (or unlog) yesterday's check-off.
  yesterday?: { done: boolean; onToggle: (done: boolean) => void }
  // Opens the caller's delete confirmation.
  onDelete?: () => void
  // Goal rows: coaches can't complete a client's task.
  canComplete?: boolean
  onDetails?: () => void
  onRename?: () => void
}

type Item = {
  key: string
  label: string
  icon: React.ReactNode
  onSelect: () => void
  tone?: 'primary' | 'danger'
}

const ICON = 'size-4 shrink-0'

export function TaskContextMenu({
  pos,
  onClose,
  title,
  canPin,
  isDone,
  isInProgress,
  isUrgent,
  isSuperUrgent,
  onSuperUrgent,
  isPinned,
  onInProgress,
  onPin,
  onComplete,
  onSkip,
  onUrgent,
  mustDo,
  yesterday,
  onDelete,
  canComplete = true,
  onDetails,
  onRename,
}: Props) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState(pos)
  // The press/right-click that opens the menu is still in flight when it mounts;
  // on touch the release lands a synthesized click on whatever is under the
  // finger. Stay inert for a beat so that opening gesture can't close us or fire
  // the item beneath the fingertip.
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    const id = setTimeout(() => setArmed(true), 300)
    return () => clearTimeout(id)
  }, [])

  // Flip/clamp so the menu never spills past the viewport edges.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    let x = pos.x
    let y = pos.y
    if (x + r.width > window.innerWidth - 8) x = window.innerWidth - r.width - 8
    if (y + r.height > window.innerHeight - 8) y = window.innerHeight - r.height - 8
    setCoords({ x: Math.max(8, x), y: Math.max(8, y) })
  }, [pos])

  // Escape closes; arrow keys move between items.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      e.preventDefault()
      const buttons = [
        ...(ref.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]') ?? []),
      ]
      if (!buttons.length) return
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
      const next =
        at === -1
          ? e.key === 'ArrowDown'
            ? 0
            : buttons.length - 1
          : (at + (e.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length
      buttons[next].focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const run = (fn: () => void) => () => {
    haptic()
    fn()
    onClose()
  }

  const groups: Item[][] = [
    [
      ...(canComplete
        ? [
            {
              key: 'complete',
              label: isDone ? t('taskMenu.markIncomplete') : t('today.markComplete'),
              icon: isDone ? <Circle className={ICON} /> : <CheckCircle2 className={ICON} />,
              onSelect: onComplete,
              tone: isDone ? undefined : ('primary' as const),
            },
          ]
        : []),
      ...(yesterday
        ? [
            {
              key: 'yesterday',
              label: yesterday.done ? t('habitYesterday.unmark') : t('habitYesterday.mark'),
              icon: <History className={ICON} />,
              onSelect: () => yesterday.onToggle(!yesterday.done),
            },
          ]
        : []),
      ...(!isDone
        ? [
            {
              key: 'progress',
              label: isInProgress ? t('taskMenu.moveBacklog') : t('taskMenu.startProgress'),
              icon: isInProgress ? (
                <CircleDashed className={ICON} />
              ) : (
                <CircleDot className={ICON} />
              ),
              onSelect: onInProgress,
            },
          ]
        : []),
    ],
    mustDo && !isDone
      ? mustDo.committed
        ? [
            {
              key: 'mustClear',
              label: t('mustDo.remove'),
              icon: <CalendarX className={ICON} />,
              onSelect: () => mustDo.onCommit(null),
            },
            {
              key: 'mustTomorrow',
              label: t('mustDo.doTomorrow'),
              icon: <CalendarClock className={ICON} />,
              onSelect: () => mustDo.onCommit('tomorrow'),
            },
          ]
        : [
            {
              key: 'mustToday',
              label: t('mustDo.doToday'),
              icon: <CalendarCheck className={ICON} />,
              onSelect: () => mustDo.onCommit('today'),
            },
            {
              key: 'mustTomorrow',
              label: t('mustDo.doTomorrow'),
              icon: <CalendarClock className={ICON} />,
              onSelect: () => mustDo.onCommit('tomorrow'),
            },
          ]
      : [],
    [
      ...(canPin
        ? [
            {
              key: 'pin',
              label: isPinned ? t('inbox.unpin') : t('inbox.pin'),
              icon: isPinned ? <PinOff className={ICON} /> : <Pin className={ICON} />,
              onSelect: onPin,
            },
          ]
        : []),
      ...(!isDone
        ? [
            {
              key: 'urgent',
              label: isUrgent ? t('taskMenu.removeUrgent') : t('taskMenu.markUrgent'),
              // Same red flag the row shows for urgent tasks.
              icon: isUrgent ? (
                <FlagOff className={ICON} />
              ) : (
                <Flag
                  className={cn(
                    ICON,
                    'fill-red-500 text-red-500 dark:fill-red-400 dark:text-red-400',
                  )}
                />
              ),
              onSelect: onUrgent,
            },
            ...(onSuperUrgent
              ? [
                  {
                    key: 'superUrgent',
                    label: isSuperUrgent
                      ? t('taskMenu.removeSuperUrgent')
                      : t('taskMenu.markSuperUrgent'),
                    icon: <UrgentFlag superUrgent className={ICON} />,
                    onSelect: onSuperUrgent,
                  },
                ]
              : []),
          ]
        : []),
    ],
    [
      ...(onDetails
        ? [
            {
              key: 'details',
              label: t('common.details'),
              icon: <Info className={ICON} />,
              onSelect: onDetails,
            },
          ]
        : []),
      ...(onRename
        ? [
            {
              key: 'rename',
              label: t('common.rename'),
              icon: <PencilLine className={ICON} />,
              onSelect: onRename,
            },
          ]
        : []),
    ],
    onSkip && !isDone
      ? [
          {
            key: 'skip',
            label: t('today.skipToday'),
            icon: <SkipForward className={ICON} />,
            onSelect: onSkip,
          },
        ]
      : [],
    onDelete
      ? [
          {
            key: 'delete',
            label: t('inbox.deleteEllipsis'),
            icon: <Trash2 className={ICON} />,
            onSelect: onDelete,
            tone: 'danger' as const,
          },
        ]
      : [],
  ].filter((g) => g.length > 0)

  return createPortal(
    // Full-screen catcher: any press/right-click outside the menu closes it.
    <div
      className="fixed inset-0 z-[60]"
      onPointerDown={() => {
        if (armed) onClose()
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        if (armed) onClose()
      }}
    >
      <div
        ref={ref}
        role="menu"
        aria-label={title}
        style={{ left: coords.x, top: coords.y }}
        onPointerDown={(e) => e.stopPropagation()}
        className="fixed flex w-60 max-w-[calc(100vw-16px)] select-none flex-col rounded-xl border border-border/70 bg-popover p-1 text-popover-foreground shadow-xl duration-100 animate-in fade-in-0 zoom-in-95"
      >
        {title && (
          <p className="truncate px-2.5 pt-1.5 pb-1 text-[11.5px] font-medium text-muted-foreground">
            {title}
          </p>
        )}
        {groups.map((group, gi) => (
          <div
            key={group[0].key}
            className={cn('flex flex-col', gi > 0 && 'mt-1 border-t border-border/60 pt-1')}
          >
            {group.map((it) => (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                onClick={armed ? run(it.onSelect) : undefined}
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium outline-none transition-colors hover:bg-muted focus-visible:bg-muted',
                  it.tone === 'primary' && 'text-primary-text',
                  it.tone === 'danger' && 'text-destructive',
                )}
              >
                <span className={cn('flex', it.tone ? '' : 'text-muted-foreground')}>
                  {it.icon}
                </span>
                {it.label}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>,
    document.body,
  )
}
